import { InputDatePicker, InputDropdown, InputRow, InputValue } from "@/components/common/inputs";
import type { ScheduleType, ScheduledParticipantResponsible, PhotoMaterialType } from "@/api/schedule/models";
import type { UseFormControl } from "@/hooks/common/use-form";
import { scheduleTimeOptions } from "@/utils/features/schedule/options";
import { formatTalentAge } from "@/utils/features/talent/validations";
import type { FC, ReactNode } from "react";

export type ScheduleFormProps = {
  control: UseFormControl;
  mode: "create" | "reschedule" | "reschedulePhotoRenewal";
  isMinor: boolean;
  photoRenewal?: {
    scheduleType: ScheduleType;
    photoMaterialType: PhotoMaterialType | null;
    talentCode: string;
    responsibles: ScheduledParticipantResponsible[];
  };
  minimumDate: Date;
  maximumDate: Date;
  popperContainer?: FC<{ children?: ReactNode }>;
  usePortalMenu?: boolean;
};

export function ScheduleForm({
  control,
  isMinor,
  minimumDate,
  maximumDate,
  popperContainer,
  usePortalMenu,
  mode,
  photoRenewal,
}: Readonly<ScheduleFormProps>): JSX.Element {
  const isReschedule = mode === "reschedule";
  const isPhotoRenewalReschedule = mode === "reschedulePhotoRenewal";
  const candidateBirthday = control.watch("candidateBirthday") as Date | string | null;
  const candidateAge =
    !isReschedule && !isPhotoRenewalReschedule && candidateBirthday ? formatTalentAge(candidateBirthday) : "";
  const selectedResponsibleId = control.watch("responsibleId") as string | undefined;
  const selectedResponsible = photoRenewal?.responsibles.find(({ id }) => id === selectedResponsibleId);
  const showCandidateContacts = isReschedule || !isMinor || isPhotoRenewalReschedule;

  if (isPhotoRenewalReschedule && photoRenewal) {
    return (
      <>
        <section>
          <h2 className="mb-4 text-base font-medium text-title">Dados do agendamento</h2>
          <InputRow>
            <InputDropdown
              control={control}
              field="scheduleType"
              label="Categoria"
              options={[{ label: "Renovação de fotos", value: photoRenewal.scheduleType }]}
              disabled
            />
            <InputValue
              control={control}
              field="photoMaterialType"
              label="Material contratado"
              value={photoRenewal.photoMaterialType ?? ""}
              disabled
            />
          </InputRow>
        </section>
        <section>
          <h2 className="mb-4 text-base font-medium text-title">Dados do talento</h2>
          <InputRow>
            <InputValue
              control={control}
              field="talentCode"
              label="Código do talento"
              value={photoRenewal.talentCode}
              disabled
            />
            <InputValue control={control} field="candidateFullName" label="Nome completo" disabled />
          </InputRow>
          <InputRow className="mt-4">
            <InputDatePicker control={control} field="candidateBirthday" label="Data de nascimento" disabled />
          </InputRow>
        </section>
        {!isMinor && (
          <section>
            <h2 className="mb-4 text-base font-medium text-title">Contatos</h2>
            <InputRow>
              <InputValue control={control} field="candidateEmail" label="Email" disabled />
              <InputValue control={control} field="candidatePhone" label="Telefone 1 (WhatsApp)" disabled />
            </InputRow>
            <InputValue
              control={control}
              field="candidateSecondaryPhone"
              label="Telefone 2"
              disabled
              className="mt-4"
            />
          </section>
        )}
        {isMinor && (
          <section>
            <h2 className="mb-4 text-base font-medium text-title">Responsável pelo Talento</h2>
            <InputDropdown
              control={control}
              field="responsibleId"
              label="Responsável"
              options={photoRenewal.responsibles.map(({ id, fullName }) => ({ value: id ?? "", label: fullName }))}
              noOptionSelectedText="Selecionar"
            />
            <InputRow className="mt-4">
              <InputValue
                control={control}
                field="responsibleEmail"
                label="Email"
                value={selectedResponsible?.email ?? ""}
                disabled
              />
              <InputValue
                control={control}
                field="responsiblePhone"
                label="Telefone 1 (WhatsApp)"
                value={selectedResponsible?.phone ?? ""}
                disabled
              />
            </InputRow>
            <InputValue
              control={control}
              field="responsibleSecondaryPhone"
              label="Telefone 2"
              value={selectedResponsible?.secondaryPhone ?? ""}
              disabled
              className="mt-4"
            />
          </section>
        )}
        <ScheduleDateTimeFields
          control={control}
          minimumDate={minimumDate}
          maximumDate={maximumDate}
          popperContainer={popperContainer}
          usePortalMenu={usePortalMenu}
        />
      </>
    );
  }

  return (
    <>
      <section>
        <h2 className="mb-4 text-base font-medium text-title">Dados do talento</h2>
        <InputRow>
          <InputValue
            control={control}
            field="candidateFullName"
            label="Nome completo"
            placeholder="Ex: Maria Mcfinney"
            disabled={isReschedule}
          />
          <InputDatePicker
            control={control}
            field="candidateBirthday"
            label="Data de nascimento"
            placeholder="DD/MM/AAAA"
            disabled={isReschedule}
            hideRemoveFilterButton
            popperContainer={popperContainer}
            popperClassName={popperContainer ? "!z-[1000]" : undefined}
            rightContent={candidateAge && <span className="text-primary text-xs font-medium">{candidateAge}</span>}
          />
        </InputRow>
        {showCandidateContacts && <CandidateContactFields control={control} />}
      </section>

      {isMinor && <ResponsibleFields control={control} disableFullName={isReschedule} />}

      <ScheduleDateTimeFields
        control={control}
        minimumDate={minimumDate}
        maximumDate={maximumDate}
        popperContainer={popperContainer}
        usePortalMenu={usePortalMenu}
      />
    </>
  );
}

function ScheduleDateTimeFields({
  control,
  minimumDate,
  maximumDate,
  popperContainer,
  usePortalMenu,
}: Pick<
  ScheduleFormProps,
  "control" | "minimumDate" | "maximumDate" | "popperContainer" | "usePortalMenu"
>): JSX.Element {
  return (
    <section>
      <h2 className="mb-4 text-base font-medium text-title">Agendamento</h2>
      <InputRow>
        <InputDatePicker
          control={control}
          field="scheduleDate"
          label="Data"
          placeholder="DD/MM/AAAA"
          minDate={minimumDate}
          maxDate={maximumDate}
          hideRemoveFilterButton
          popperContainer={popperContainer}
          popperClassName={popperContainer ? "!z-[1000]" : undefined}
        />
        <InputDropdown
          control={control}
          field="scheduleTime"
          label="Horário"
          options={scheduleTimeOptions}
          noOptionSelectedText="Selecionar"
          usePortalMenu={usePortalMenu}
          menuClassName={usePortalMenu ? "!z-[1000]" : undefined}
        />
      </InputRow>
    </section>
  );
}

function CandidateContactFields({ control }: { control: UseFormControl }): JSX.Element {
  return (
    <>
      <InputRow className="mt-4">
        <InputValue control={control} field="candidateEmail" label="Email" placeholder="Ex: email@exemplo.com.br" />
        <InputValue
          control={control}
          field="candidatePhone"
          label="Telefone 1 (WhatsApp)"
          placeholder="Ex: (00) 00000-0000"
          mask="brazilian-phone"
          maxLength={15}
        />
      </InputRow>
      <InputValue
        control={control}
        field="candidateSecondaryPhone"
        label="Telefone 2"
        placeholder="Ex: (00) 00000-0000"
        mask="brazilian-phone"
        maxLength={15}
        className="mt-4"
      />
    </>
  );
}

function ResponsibleFields({
  control,
  disableFullName,
}: {
  control: UseFormControl;
  disableFullName: boolean;
}): JSX.Element {
  return (
    <section>
      <h2 className="mb-4 text-base font-medium text-title">Responsável pelo Talento</h2>
      <InputRow>
        <InputValue
          control={control}
          field="responsibleFullName"
          label="Nome completo"
          placeholder="Ex: Maria Mcfinney"
          disabled={disableFullName}
        />
        <InputValue control={control} field="responsibleEmail" label="Email" placeholder="Ex: email@exemplo.com.br" />
      </InputRow>
      <InputRow className="mt-4">
        <InputValue
          control={control}
          field="responsiblePhone"
          label="Telefone 1 (WhatsApp)"
          placeholder="Ex: (00) 00000-0000"
          mask="brazilian-phone"
          maxLength={15}
        />
        <InputValue
          control={control}
          field="responsibleSecondaryPhone"
          label="Telefone 2"
          placeholder="Ex: (00) 00000-0000"
          mask="brazilian-phone"
          maxLength={15}
        />
      </InputRow>
    </section>
  );
}
